import type { MarketingNavFeatured } from "@/data/navigation";
import { cn } from "@/lib/utils";

export type MarketingNavMatchable = {
	href: string;
	isActive?: (pathname: string) => boolean;
	contributesToGroupActive?: boolean;
};

export function marketingNavItemActive(
	pathname: string,
	href: string,
): boolean {
	if (href === "/") {
		return pathname === "/";
	}
	return pathname === href || pathname.startsWith(`${href}/`);
}

function marketingNavItemMatches(
	pathname: string,
	item: MarketingNavMatchable,
): boolean {
	if (item.isActive) {
		return item.isActive(pathname);
	}
	return marketingNavItemActive(pathname, item.href);
}

/** True only for the most specific matching sibling (avoids /docs + /docs/api-reference both active). */
export function marketingNavSiblingActive(
	pathname: string,
	item: MarketingNavMatchable,
	siblings: MarketingNavMatchable[],
): boolean {
	const matching = siblings.filter((sibling) =>
		marketingNavItemMatches(pathname, sibling),
	);
	if (matching.length === 0) {
		return false;
	}
	const best = matching.reduce((longest, current) =>
		current.href.length >= longest.href.length ? current : longest,
	);
	return best.href === item.href;
}

function marketingNavGroupLinkMatches(
	pathname: string,
	link: MarketingNavMatchable,
): boolean {
	if (link.contributesToGroupActive === false) {
		return false;
	}
	return marketingNavItemMatches(pathname, link);
}

export function marketingNavGroupActive(
	pathname: string,
	item: {
		href?: string;
		items?: MarketingNavMatchable[];
		featured?: MarketingNavMatchable[];
	},
): boolean {
	if (item.href && marketingNavItemActive(pathname, item.href)) {
		return true;
	}

	if (
		item.items?.some((link) => marketingNavGroupLinkMatches(pathname, link))
	) {
		return true;
	}

	return (
		item.featured?.some((card) =>
			marketingNavGroupLinkMatches(pathname, card),
		) ?? false
	);
}

/** Single weight for all nav states (uidotsh navigation guideline). */
const navItemFontClass = "font-medium";

const desktopNavActiveClass = cn(
	"text-foreground bg-muted/70",
	"light:bg-accent/45",
	"dark:bg-white/8",
);

const desktopNavInactiveClass = cn(
	"text-muted-foreground hover:text-foreground hover:bg-muted/45",
	"light:hover:bg-accent/25",
	"dark:hover:bg-white/5",
);

/** Shared base for triggers and top-level links. */
export const marketingNavControlBaseClass = cn(
	"inline-flex items-center justify-center transition-[color,background-color] duration-200 outline-none",
	navItemFontClass,
	"focus-visible:ring-2 focus-visible:ring-ring/40",
);

/** Desktop dropdown trigger. */
export function marketingNavTriggerClassName(
	pathname: string,
	item: { href?: string; items?: { href: string }[] },
): string {
	const active = marketingNavGroupActive(pathname, item);

	return cn(
		marketingNavControlBaseClass,
		"h-7 rounded-full px-2 py-0.5 text-sm/5 xl:px-2.5",
		"bg-transparent shadow-none",
		"[&_svg]:opacity-60 [&_svg]:transition-opacity",
		"data-popup-open:[&_svg]:opacity-100 data-open:[&_svg]:opacity-100",
		active ? desktopNavActiveClass : desktopNavInactiveClass,
		"data-popup-open:bg-muted/60 data-open:bg-muted/60",
		"light:data-popup-open:bg-accent/35 light:data-open:bg-accent/35",
		"data-popup-open:text-foreground data-open:text-foreground",
	);
}

/** Mobile sheet group disclosure trigger. */
export function marketingNavMobileGroupTriggerClassName(
	pathname: string,
	item: { href?: string; items?: { href: string }[] },
): string {
	const active = marketingNavGroupActive(pathname, item);

	return cn(
		marketingNavControlBaseClass,
		"w-full justify-between rounded-lg px-3 py-2.5 text-base/7 sm:text-sm/6",
		active
			? cn("text-foreground bg-muted/50", "light:bg-accent/30")
			: cn(
					"text-muted-foreground hover:bg-muted/40 hover:text-foreground",
					"light:hover:bg-accent/20",
				),
	);
}

/** Sub-link styles inside dropdown panels and mobile sheet. */
export function marketingNavSubLinkClassName(
	pathname: string,
	hrefOrLink: string | MarketingNavMatchable,
	siblings?: MarketingNavMatchable[],
): string {
	const link: MarketingNavMatchable =
		typeof hrefOrLink === "string" ? { href: hrefOrLink } : hrefOrLink;
	const active =
		siblings && siblings.length > 0
			? marketingNavSiblingActive(pathname, link, siblings)
			: marketingNavItemMatches(pathname, link);

	return cn(
		"rounded-md px-2.5 py-2 transition-colors outline-none",
		navItemFontClass,
		"focus-visible:ring-2 focus-visible:ring-ring/35",
		active
			? cn(
					"text-foreground bg-muted/70",
					"light:bg-accent/40",
					"dark:bg-white/8",
				)
			: cn(
					"text-muted-foreground hover:text-foreground hover:bg-muted/45",
					"light:hover:bg-accent/25",
					"dark:hover:bg-white/5",
				),
	);
}

/** Dropdown mega-menu panel (desktop). */
export const marketingNavMegaPanelClass = cn(
	"flex h-full w-[min(100vw-2rem,42rem)] overflow-hidden",
	"light:bg-card",
);

/** Simple text link in mega-menu columns. */
export function marketingNavMegaSimpleLinkClassName(
	pathname: string,
	link: MarketingNavMatchable,
	siblings: MarketingNavMatchable[],
): string {
	const active = marketingNavSiblingActive(pathname, link, siblings);

	return cn(
		"block rounded-md px-2.5 py-2 text-sm/5 transition-colors outline-none",
		navItemFontClass,
		"focus-visible:ring-2 focus-visible:ring-ring/35",
		active
			? cn(
					"text-foreground bg-muted/70",
					"light:bg-accent/40",
					"dark:bg-white/8",
				)
			: cn(
					"text-muted-foreground hover:text-foreground hover:bg-muted/45",
					"light:hover:bg-accent/25",
					"dark:hover:bg-white/5",
				),
	);
}

/** Featured card in mega-menu. */
export function marketingNavFeaturedCardClassName(
	pathname: string,
	card: MarketingNavMatchable,
	siblings: MarketingNavMatchable[],
): string {
	const active = marketingNavSiblingActive(pathname, card, siblings);

	return cn(
		"group/card relative flex min-h-28 w-full flex-1 flex-col justify-center overflow-hidden rounded-xl border p-4 transition-colors outline-none",
		"focus-visible:ring-2 focus-visible:ring-ring/40",
		active
			? cn(
					"border-border/70 bg-muted/50",
					"light:bg-accent/30",
					"dark:border-white/15 dark:bg-white/8",
				)
			: cn(
					"border-border/50 bg-muted/30 hover:border-border/70 hover:bg-muted/45",
					"light:hover:bg-accent/20",
					"dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/8",
				),
	);
}

export const marketingNavFeaturedCardContentClass =
	"relative z-1 flex items-center gap-3";

export const marketingNavFeaturedCardBadgeClass = cn(
	"text-sm/5 text-muted-foreground",
);

export const marketingNavFeaturedCardTitleClass = cn(
	"text-sm/5 text-foreground",
	navItemFontClass,
);

export const marketingNavFeaturedCardDescriptionClass = cn(
	"text-sm/5 text-muted-foreground",
);

/** Decorative backgrounds for featured cards. */
export function marketingNavFeaturedPatternClass(
	pattern: MarketingNavFeatured["pattern"] = "grid",
): string {
	return cn(
		"pointer-events-none absolute inset-0 opacity-40 transition-opacity group-hover/card:opacity-55",
		pattern === "grid" &&
			"bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-size-[1.25rem_1.25rem]",
		pattern === "orbit" &&
			"bg-[radial-gradient(circle_at_70%_30%,var(--primary)_0%,transparent_45%),radial-gradient(circle_at_30%_70%,var(--border)_0%,transparent_40%)]",
		pattern === "stack" &&
			"bg-[linear-gradient(135deg,transparent_40%,var(--border)_40%,var(--border)_42%,transparent_42%),linear-gradient(135deg,transparent_55%,var(--muted-foreground)_55%,var(--muted-foreground)_56%,transparent_56%)]",
		pattern === "shield" &&
			"bg-[radial-gradient(ellipse_at_80%_20%,var(--primary)_0%,transparent_50%)]",
	);
}

/** Recessed pill tray for desktop marketing nav. */
export const marketingNavTrayClassName = cn(
	"flex w-max max-w-[calc(100vw-12rem)] shrink-0 items-center gap-px overflow-x-auto rounded-full p-0.5",
	"[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
	"border border-border/45 bg-muted/25",
	"light:border-border/50 light:bg-white/45",
	"light:shadow-[inset_0_1px_1px_rgba(0,0,0,0.03)]",
	"light:ring-1 light:ring-inset light:ring-white/80",
	"dark:border-white/10 dark:bg-black/30 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
);

/** Desktop top-level link in the pill tray. */
export function marketingNavLinkClassName(
	pathname: string,
	href: string,
): string {
	const active = marketingNavItemActive(pathname, href);

	return cn(
		marketingNavControlBaseClass,
		"h-7 rounded-full px-2 py-0.5 text-sm/5 xl:px-2.5",
		active ? desktopNavActiveClass : desktopNavInactiveClass,
	);
}

/** Mobile sheet top-level link. */
export function marketingNavMobileLinkClassName(
	pathname: string,
	href: string,
): string {
	const active = marketingNavItemActive(pathname, href);

	return cn(
		marketingNavControlBaseClass,
		"w-full justify-start rounded-lg px-3 py-2.5 text-base/7 sm:text-sm/6",
		active
			? cn("text-foreground bg-muted/50", "light:bg-accent/30")
			: cn(
					"text-muted-foreground hover:bg-muted/40 hover:text-foreground",
					"light:hover:bg-accent/20",
				),
	);
}

/** Title line in dropdown / mobile sub-links (block-level). */
export const marketingNavSubLinkTitleClass = cn(
	"text-sm/5 text-foreground",
	navItemFontClass,
);

/** Description copy under sub-link titles (block-level). */
export const marketingNavSubLinkDescriptionClass = cn(
	"line-clamp-2 text-sm/5 text-muted-foreground",
	"light:text-muted-foreground/80",
);

/** Mobile description under sub-link titles. */
export const marketingNavSubLinkDescriptionMobileClass = cn(
	"line-clamp-2 text-base/7 text-muted-foreground sm:text-sm/5",
	"light:text-muted-foreground/80",
);

/** Header auth actions — aligned with nav ghost styling. */
export const marketingHeaderActionLinkClass = cn(
	"inline-flex items-center justify-center rounded-full px-3 py-2 text-sm/6 font-medium text-muted-foreground transition-colors",
	"hover:bg-muted/50 hover:text-foreground",
	"light:hover:bg-accent/25",
);
